/**
 * 네이버 지도 v3 SDK 동적 로더.
 *
 * SDK는 전역 `window.naver` 를 만든다.
 * 같은 페이지에서 두 번 로드되지 않도록 promise 캐싱.
 *
 * NCP 키 형식 두 가지를 모두 지원:
 * - 신: VITE_NAVER_MAP_KEY_ID    → ncpKeyId 파라미터
 * - 구: VITE_NAVER_MAP_CLIENT_ID → ncpClientId 파라미터
 *
 * 둘 다 있으면 KEY_ID 우선.
 */

let loadingPromise = null;

export function loadNaverMaps() {
  if (typeof window !== "undefined" && window.naver?.maps) {
    return Promise.resolve(window.naver.maps);
  }
  if (loadingPromise) return loadingPromise;

  const keyId = import.meta.env.VITE_NAVER_MAP_KEY_ID;
  const clientId = import.meta.env.VITE_NAVER_MAP_CLIENT_ID;

  let paramName, paramValue;
  if (keyId && !keyId.startsWith("여기에")) {
    paramName = "ncpKeyId";
    paramValue = keyId;
  } else if (clientId && !clientId.startsWith("여기에")) {
    paramName = "ncpClientId";
    paramValue = clientId;
  } else {
    return Promise.reject(
      new Error(
        "VITE_NAVER_MAP_KEY_ID 또는 VITE_NAVER_MAP_CLIENT_ID 가 설정되지 않았습니다. " +
          "프로젝트 루트에 .env.local 파일을 만들고 본인 NCP 키를 입력해주세요."
      )
    );
  }

  loadingPromise = new Promise((resolve, reject) => {
    // SDK 자체의 인증 실패 메시지를 가로채기 위해 임시로 alert 후킹
    const originalAlert = window.alert;
    let authFailed = false;
    window.alert = function (msg) {
      if (typeof msg === "string" && msg.includes("인증")) {
        authFailed = true;
        console.error("[Naver Map] 인증 실패:", msg);
        reject(
          new Error(
            "네이버 지도 인증 실패. NCP 콘솔에서 (1) Web Dynamic Map 활성화 여부, " +
              `(2) 서비스 URL 에 '${window.location.origin}' 가 등록되어 있는지, ` +
              "(3) 키 형식이 신/구 중 어느 것인지 확인해주세요. " +
              "현재 사용중인 파라미터: " +
              paramName
          )
        );
        return;
      }
      return originalAlert.apply(window, arguments);
    };

    const script = document.createElement("script");
    script.src =
      "https://oapi.map.naver.com/openapi/v3/maps.js" +
      `?${paramName}=${encodeURIComponent(paramValue)}` +
      "&submodules=geocoder";
    script.async = true;

    script.onload = () => {
      // alert이 동기적으로 호출되면 이미 authFailed가 true임
      // 짧은 지연 후 체크 (SDK 내부 인증 호출이 끝날 시간)
      setTimeout(() => {
        window.alert = originalAlert; // 복구
        if (authFailed) return; // 이미 reject 됨
        if (window.naver?.maps) {
          resolve(window.naver.maps);
        } else {
          reject(
            new Error("네이버 지도 SDK가 로드됐지만 window.naver.maps 가 없습니다.")
          );
        }
      }, 200);
    };

    script.onerror = () => {
      window.alert = originalAlert;
      loadingPromise = null;
      reject(
        new Error(
          "네이버 지도 SDK 스크립트 로드 실패. 네트워크 또는 NCP 콘솔 설정을 확인해주세요."
        )
      );
    };

    document.head.appendChild(script);
  });

  return loadingPromise;
}

/** 제주도 좌표 범위 (panning clamp) */
export const JEJU_BOUNDS = {
  south: 33.10,
  north: 33.60,
  west: 126.10,
  east: 126.98,
};

/** 제주도 대략 중심 좌표 */
export const JEJU_CENTER = { lat: 33.379, lng: 126.545 };

/** 초기 줌 / 최소 줌 / 최대 줌
 * max: 네이버 SDK 한계는 21 이지만 너무 깊으면 건물 입구까지 보임. 20 이 현실적인 리믹.
 */
export const ZOOM = {
  initial: 10,
  min: 9,
  max: 20,
};

/** 주어진 좌표가 제주 범위 안인지 */
export function isInJeju(lat, lng) {
  if (lat == null || lng == null) return false;
  return (
    lat >= JEJU_BOUNDS.south && lat <= JEJU_BOUNDS.north &&
    lng >= JEJU_BOUNDS.west && lng <= JEJU_BOUNDS.east
  );
}

/**
 * 현재 위치 요청. Geolocation API 래퍼.
 * 권한 거절 시 reject. 제주 범위 밖이더라도 좌표는 반환 (사용 측에서 판단).
 */
export function requestUserLocation(options = {}) {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.reject(new Error("이 브라우저는 Geolocation을 지원하지 않습니다."));
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy }),
      (err) => reject(err),
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000,
        ...options,
      }
    );
  });
}

/**
 * 좌표 → 동네 이름 (reverse geocoding)
 * 네이버 지도 Service.reverseGeocode 사용. submodules=geocoder 로딩 필수 (이미 포함됨).
 * 반환:
 *   { regionName, area1, area2, area3, fullAddress }
 *   regionName 은 area3 (동/리) > area2 (시/군/구) > area1 (도) 순으로 fallback
 */
export function reverseGeocode(lat, lng) {
  return new Promise((resolve, reject) => {
    if (!window.naver?.maps?.Service) {
      reject(new Error("네이버 지도 Service 모듈이 로드되지 않았습니다."));
      return;
    }
    const maps = window.naver.maps;
    // OrderType 상수명이 SDK 버전마다 다를 수 있어서 문자열 직접 사용
    // (ADMIN_CODE ✕, ADM_CODE ✓ — undefined 이면 잘못된 orders 파라미터로 400)
    maps.Service.reverseGeocode(
      {
        coords: new maps.LatLng(lat, lng),
        orders: "legalcode,admcode,addr,roadaddr",
      },
      (status, response) => {
        if (status !== maps.Service.Status.OK) {
          reject(new Error("Reverse geocoding 실패: status=" + status));
          return;
        }
        const results = response?.v2?.results || [];
        // 행정동(admcode)을 우선 — "이도이동"(법정동 한자식) 보다 "이도2동"(행정동)이 자연스러움
        const result = results.find((r) => r.name === "admcode") || results[0];
        if (!result) {
          reject(new Error("주소 결과 없음"));
          return;
        }
        const region = result.region || {};
        const area1 = region.area1?.name || null; // 도 (예: "제주특별자치도")
        const area2 = region.area2?.name || null; // 시/군/구 (예: "제주시")
        const area3 = region.area3?.name || null; // 동/읍/면 (예: "이도2동")
        // 시/군 + 동을 같이 (예: "제주시 이도2동"). area3가 없으면 area2 단독, 그와도 없으면 area1
        const regionName =
          [area2, area3].filter(Boolean).join(" ") || area1 || "현재 위치";
        const fullAddress = [area1, area2, area3].filter(Boolean).join(" ");
        resolve({ regionName, area1, area2, area3, fullAddress });
      }
    );
  });
}
